import { Suspense } from 'react'

import { QueryErrorResetBoundary } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'

import { Skeleton } from '@/components/ui'
import { useListarLeadsRepresados } from '@/features/clientes/hooks/use-listar-leads-represados'
import { useEmpreendimentosDisponiveis } from '@/features/empreendimentos/brasil/hooks/use-empreendimentos-disponiveis'
import { useSearchParamsWithTimestamp } from '@/hooks'
import { showToast } from '@/infra/sonner'
import { Page, type IBreadcrumbItem } from '@/layouts/page'
import {
  LeadsRepresadosFiltros,
  LeadsRepresadosFiltrosSkeleton,
} from '@/pages/consulta-leads-represados/ui/filtros-lead-represado'
import { ListaLeadsRepresados } from '@/pages/consulta-leads-represados/ui/lista-leads-represados'
import { ListaLeadsRepresadosFooter } from '@/pages/consulta-leads-represados/ui/lista-leads-represados-footer'
import { ListaLeadsRepresadosSkeleton } from '@/pages/consulta-leads-represados/ui/lista-leads-represados-skeleton'
import { ApiErrorDisplay } from '@/shared/api-error-display'

export const ConsultaLeadsRepresadosPage = () => {
  const { t } = useTranslation('leads-represados')
  const { filters } = useSearchParamsWithTimestamp()
  const { data: leadsRepresados } = useListarLeadsRepresados(filters, true)
  const { refetch, isFetching } = useEmpreendimentosDisponiveis({ apenasFuturo: true, enabled: false })
  const navigate = useNavigate()
  const location = useLocation()
  const state: {
    breadcrumbDetalhesEmpreendimento?: IBreadcrumbItem[]
  } = location.state

  const screenBreadcrumb = [{ label: t('pages.consulta.breadcrumb') }]

  const breadcrumb: IBreadcrumbItem[] = state?.breadcrumbDetalhesEmpreendimento
    ? [...state.breadcrumbDetalhesEmpreendimento, ...screenBreadcrumb]
    : screenBreadcrumb

  const handleCadastrarClick = async () => {
    try {
      const response = await refetch({ throwOnError: true })
      if (!response.data?.length) {
        showToast({
          variant: 'error',
          message: t('pages.consulta.footer.sem_empreendimentos_futuros'),
        })
        return
      }
      navigate('/leads-represados/cadastrar', {
        state: state?.breadcrumbDetalhesEmpreendimento
          ? {
              breadcrumbDetalhesEmpreendimento: breadcrumb.map((item) => {
                if (item.label !== t('pages.consulta.breadcrumb')) return item
                return { ...item, to: '/leads-represados', state }
              }),
            }
          : null,
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.code === AxiosError.ERR_NETWORK) return
        showToast({
          variant: 'error',
          message: t('errors_gerais.padrao', { ns: 'common' }),
        })
      }
    }
  }

  return (
    <Page breadcrumb={breadcrumb} className="mx-auto min-h-screen w-full sm:max-w-4xl md:pt-6" data-testid="home-page">
      <Page.Main>
        <Page.Header>
          <Page.Title>{t('pages.consulta.titulo')}</Page.Title>
        </Page.Header>
        <Page.Section>
          <QueryErrorResetBoundary>
            {({ reset }) => (
              <ErrorBoundary FallbackComponent={ApiErrorDisplay} onReset={reset}>
                <Suspense fallback={<LeadsRepresadosFiltrosSkeleton />}>
                  <LeadsRepresadosFiltros />
                </Suspense>
              </ErrorBoundary>
            )}
          </QueryErrorResetBoundary>
          <ListaLeadsRepresados />
        </Page.Section>
        <ListaLeadsRepresadosFooter
          totalItens={leadsRepresados?.metadata.totalDeItens ?? 0}
          paginaCorrente={leadsRepresados?.metadata.paginaCorrente ?? 1}
          onClick={handleCadastrarClick}
          isLoading={isFetching}
        />
      </Page.Main>
    </Page>
  )
}

export const ConsultaLeadsRepresadosPageSkeleton = () => {
  return (
    <div className="mx-auto min-h-screen w-full sm:max-w-4xl" data-testid="consulta-leads-represados-skeleton">
      <div className="mb-8 h-7">
        <Skeleton className="h-7 w-64" />
      </div>
      <div className="bg-foreground mt-8 pt-6">
        <LeadsRepresadosFiltrosSkeleton />
        <ListaLeadsRepresadosSkeleton />
      </div>
      <div className="bg-foreground divide-y divide-neutral-200 rounded-sm px-6 py-6">
        <Skeleton className="h-10 w-full" />
      </div>
    </div>
  )
}
